import { ConnectError } from '@bufbuild/connect';
import {
  AnyAction,
  createSlice,
  isRejectedWithValue,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  AuthState as AuthStateDTO,
  MFAStatus,
} from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { LoadingStateType, UserBadRequestError } from '@/shared/config';
import { safeJSONParse } from '@/shared/lib';

import { MFARequirementCase } from '../config';
import { isValidReportId } from '../lib/validateReportId';

import {
  confirmPassword,
  mfaDisable,
  mfaEnrollConfirm,
  mfaSignInVerify,
  mfaStatus,
  signInByPresign,
  signUpViaOrganizationInvite,
  signUpViaSalesmanInvite,
  startSignIn,
} from './authSlice.thunks';

type AuthState = {
  isReportLimitedSession: boolean;
  limitedReportId: string | null;
  isPresignSession: boolean;
  pendingMFARequirement: MFARequirementCase;
  mfaStatus: MFAStatus | null;
  mfaStatusLoading: LoadingStateType;
  // Enrol-confirm requests whose screen still shows the recovery codes.
  activeMFARecoveryCodeRequests: string[];
};

const initialState: AuthState = {
  isReportLimitedSession: false,
  limitedReportId: null,
  isPresignSession: false,
  pendingMFARequirement: null,
  mfaStatus: null,
  mfaStatusLoading: 'idle',
  activeMFARecoveryCodeRequests: [],
};

const readMFARequirement = (authState: AuthStateDTO): MFARequirementCase =>
  authState.MFA?.Requirement?.case ?? null;

// Core's mfa_required refusal triggers a status re-read, so the MFA gate can appear.
const isMFARequiredRejection = (action: AnyAction): boolean =>
  isRejectedWithValue(action) &&
  safeJSONParse((action.payload as ConnectError)?.rawMessage ?? '').code ===
    UserBadRequestError.MFA_REQUIRED;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setReportLimitedSession: (
      state,
      action: PayloadAction<{ reportId: string }>,
    ) => {
      if (isValidReportId(action.payload.reportId)) {
        state.isReportLimitedSession = true;
        state.limitedReportId = action.payload.reportId;
      }
    },
    clearReportLimitedSession: (state) => {
      state.isReportLimitedSession = false;
      state.limitedReportId = null;
    },
    setPresignSession: (state, action: PayloadAction<boolean>) => {
      state.isPresignSession = action.payload;
    },
    clearPendingMFARequirement: (state) => {
      state.pendingMFARequirement = null;
    },
    finishMFARecoveryCodes: (state, action: PayloadAction<string>) => {
      state.activeMFARecoveryCodeRequests =
        state.activeMFARecoveryCodeRequests.filter(
          (requestID) => requestID !== action.payload,
        );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startSignIn.fulfilled, (state, action) => {
        state.pendingMFARequirement = readMFARequirement(action.payload);
      })
      .addCase(confirmPassword.fulfilled, (state, action) => {
        state.pendingMFARequirement = readMFARequirement(action.payload);
      })
      .addCase(signUpViaOrganizationInvite.fulfilled, (state, action) => {
        state.pendingMFARequirement = readMFARequirement(action.payload);
      })
      .addCase(signUpViaSalesmanInvite.fulfilled, (state, action) => {
        state.pendingMFARequirement = readMFARequirement(action.payload);
      })
      // The screen that started the flow still has the recovery codes to
      // show, so it clears `pendingMFARequirement` itself when it is done.
      .addCase(mfaSignInVerify.fulfilled, (state) => {
        state.mfaStatusLoading = 'idle';
      })
      .addCase(mfaEnrollConfirm.pending, (state, action) => {
        state.activeMFARecoveryCodeRequests.push(action.meta.requestId);
      })
      .addCase(mfaEnrollConfirm.rejected, (state, action) => {
        state.activeMFARecoveryCodeRequests =
          state.activeMFARecoveryCodeRequests.filter(
            (requestID) => requestID !== action.meta.requestId,
          );
      })
      .addCase(mfaEnrollConfirm.fulfilled, (state) => {
        state.mfaStatusLoading = 'idle';
      })
      .addCase(mfaStatus.pending, (state) => {
        state.mfaStatusLoading = 'pending';
      })
      .addCase(mfaStatus.fulfilled, (state, action) => {
        state.mfaStatus = action.payload;
        state.mfaStatusLoading = 'succeeded';
      })
      .addCase(mfaStatus.rejected, (state) => {
        state.mfaStatusLoading = 'failed';
      })
      .addCase(mfaDisable.fulfilled, (state) => {
        state.mfaStatusLoading = 'idle';
      })
      .addCase(signInByPresign.fulfilled, (state, action) => {
        state.pendingMFARequirement = readMFARequirement(action.payload);

        if (
          action.meta.arg.ReportID &&
          isValidReportId(action.meta.arg.ReportID)
        ) {
          state.isReportLimitedSession = true;
          state.limitedReportId = action.meta.arg.ReportID;
        }
      })
      .addMatcher(isMFARequiredRejection, (state) => {
        // Only from a settled read: rewriting 'failed' or 'pending' here would
        // either bury the failure or start a second request.
        if (state.mfaStatusLoading === 'succeeded') {
          state.mfaStatusLoading = 'idle';
        }
      })
      .addMatcher(
        (action) => action.type.endsWith('/logout/fulfilled'),
        // The MFA status is left to `store/reset`: an 'idle' status here, while
        // the user is still loaded, makes the MFA gate re-read it anonymously.
        (state) => {
          state.isReportLimitedSession = false;
          state.limitedReportId = null;
          state.isPresignSession = false;
          state.pendingMFARequirement = null;
          state.activeMFARecoveryCodeRequests = [];
        },
      );
  },
});

export const actions = authSlice.actions;

export default authSlice.reducer;
